import React, { useEffect, useState } from 'react';
import { ConfigurationSection, SpaceBetweenRow } from "@features/settings/pages/StyleComponents";
import { BodyLarge, BodyRegular } from "../../atoms/fonts/Body";
import Colours from "../../atoms/Colours";
import { Switch } from "../../atoms/Switch";
import { useDispatch } from "react-redux";
import { failureNotification } from '@shared/store/notification.duck';
import { SimpleSpinner } from "../../atoms/SimpleSpinner";
import { useTranslation } from "react-i18next";
import { ReportRecipientType, ReportSchedule, ReportType, defaultReportSchedule, scheduleFromConfig } from '@shared/models/report.models';
import { getReportConfigs, updateReportConfig } from '@features/settings/services/ReportService';
import { GenericDropDownList } from "../../molecules/GenericDropDownList";
import { SendSampleReportButton } from "./SendSampleReportButton";
import { ReportScheduleSetting } from "./ReportScheduleSetting";

export function MissingWhereaboutsReportSetting() {
  const [isLoading, setIsLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [recipientType, setRecipientType] = useState<ReportRecipientType>(ReportRecipientType.COMPANY_ADMINS);
  const [schedule, setSchedule] = useState<ReportSchedule>(defaultReportSchedule(ReportType.MISSING_WHEREABOUTS_SUMMARY));
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    let active = true;
    getReportConfigs()
      .then(configs => {
        if (!active) {
          return;
        }
        const config = configs.find(c => c.reportType === ReportType.MISSING_WHEREABOUTS_SUMMARY);
        if (config) {
          setEnabled(config.enabled);
          if (config.recipientTypes && config.recipientTypes.length > 0) {
            setRecipientType(config.recipientTypes[0]);
          }
          setSchedule(scheduleFromConfig(config, ReportType.MISSING_WHEREABOUTS_SUMMARY));
        }
      })
      .catch(() => { /* leave defaults in place */ });
    return () => { active = false; };
  }, []);

  const persist = async (next: { enabled: boolean; recipientType: ReportRecipientType; schedule: ReportSchedule }) => {
    try {
      setIsLoading(true);
      await updateReportConfig({
        reportType: ReportType.MISSING_WHEREABOUTS_SUMMARY,
        enabled: next.enabled,
        recipientTypes: [next.recipientType],
        ...next.schedule,
      });
      setEnabled(next.enabled);
      setRecipientType(next.recipientType);
      setSchedule(next.schedule);
    } catch (e: any) {
      dispatch(failureNotification('Failed to update setting, please wait a minute.'));
    } finally {
      setIsLoading(false);
    }
  };

  const onToggle = (value: boolean) => persist({ enabled: value, recipientType, schedule });

  const onRecipientChange = (value: ReportRecipientType) => {
    if (value === recipientType) {
      return;
    }
    persist({ enabled, recipientType: value, schedule });
  };

  const onScheduleChange = (value: ReportSchedule) => persist({ enabled, recipientType, schedule: value });

  const recipientItems = [
    { id: ReportRecipientType.COMPANY_ADMINS, name: t('settings.missing-whereabouts.recipients.company-admins') },
    { id: ReportRecipientType.TEAM_ADMINS, name: t('settings.missing-whereabouts.recipients.team-admins') },
    { id: ReportRecipientType.LINE_MANAGERS, name: t('settings.missing-whereabouts.recipients.line-managers') },
  ];

  return (
    <ConfigurationSection>
      <SpaceBetweenRow>
        <BodyLarge colour={Colours.blue}>{t('settings.missing-whereabouts.title')}</BodyLarge>
        {isLoading ? <SimpleSpinner size={32} /> : <Switch value={enabled} label="" onChange={onToggle} />}
      </SpaceBetweenRow>
      <BodyRegular style={{ marginBottom: 16 }}>{t('settings.missing-whereabouts.subtitle')}</BodyRegular>
      <SendSampleReportButton reportType={ReportType.MISSING_WHEREABOUTS_SUMMARY} />

      {enabled && (
        <>
          <BodyRegular weight={600} style={{ marginBottom: 8 }}>{t('settings.missing-whereabouts.recipients.label')}</BodyRegular>
          <GenericDropDownList selectedId={recipientType}
                               items={recipientItems}
                               placeholder={'settings.missing-whereabouts.recipients.placeholder'}
                               disabled={isLoading}
                               onSelect={onRecipientChange} />
          <ReportScheduleSetting schedule={schedule}
                                 disabled={isLoading}
                                 noteKey={'settings.missing-whereabouts.schedule-note'}
                                 onChange={onScheduleChange} />
        </>
      )}
    </ConfigurationSection>
  );
}
